/**
 * Inline deal-history panel for the SOP TODO drawer.
 *
 * When the rep clicks "Show Deal History" in the drawer header, this
 * panel renders at the top of the scroll area — same chrome as the
 * action blocks below (light bordered card, slightly muted) so it
 * reads as part of the conversation. Ordered oldest → newest so the
 * scroll flow matches reading an email thread.
 *
 * Three item kinds woven together (matches PD's "All" timeline view):
 *
 *   - ``activity`` — calls / emails / meetings / tasks / deadlines from
 *     ``processed.pd_activities_current``.
 *   - ``note`` — PD's free-form deal notes from ``landing.pipedrive_raw``;
 *     rendered with a yellow background to match PD's UI.
 *   - ``email`` — inbound / outbound Gmail messages routed via the
 *     canonical lead.
 *
 * Timestamps use ``display_timestamp`` (computed server-side): for done
 * activities the completion time, for un-done activities the planned
 * due date (so a future-planned call reads "in 2 days" rather than
 * "2 days ago" off of add_time), and for notes / emails the event
 * time. Overdue activities (un-done + due_date < today) get a red
 * border + "Overdue" badge.
 */

import { addDays, formatDistanceToNowStrict, parseISO, subDays } from "date-fns";
import DOMPurify from "dompurify";
import {
  AlertTriangleIcon,
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  CheckCircle2Icon,
  ClockIcon,
  HistoryIcon,
  Loader2Icon,
  MailIcon,
  StickyNoteIcon,
} from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useDealHistory, type DealHistoryItem } from "@/hooks/use-agent-todos";


// Date formatters in business timezone (Eastern). The drawer renders
// every history timestamp with a relative label ("3 months ago") and
// the absolute date/time on hover so the rep can disambiguate without
// leaving the surface. Two formatters because Intl doesn't expose an
// "at" separator natively — concatenate ourselves.
const _BUSINESS_TZ = "America/New_York";
const _DATE_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: _BUSINESS_TZ,
  month: "short",
  day: "numeric",
  year: "numeric",
});
const _TIME_FMT = new Intl.DateTimeFormat("en-US", {
  timeZone: _BUSINESS_TZ,
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
});
// YYYY-MM-DD in business timezone — used to build the
// ``/conversations`` URL window. ``en-CA`` is the locale that gives
// ISO-shaped dates ("2026-04-21") natively without manual padding.
const _DATE_KEY_FMT = new Intl.DateTimeFormat("en-CA", {
  timeZone: _BUSINESS_TZ,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

function formatAbsolute(iso: string): string {
  const d = new Date(iso);
  return `${_DATE_FMT.format(d)} at ${_TIME_FMT.format(d)}`;
}

/** Build the ``/conversations?callId=…&from=…&to=…`` URL for a CALL
 *  chip. ``from`` / ``to`` are YYYY-MM-DD bracketing the call by ±1
 *  day in business time — the conversations page's date filter is
 *  day-granular, so a single-day window can miss a call that crosses
 *  midnight or arrives slightly skewed in ingestion. */
function callsConversationUrl(callId: string, iso: string | null): string {
  const base = `/conversations?callId=${encodeURIComponent(callId)}`;
  if (!iso) return base;
  const ts = parseISO(iso);
  const from = _DATE_KEY_FMT.format(subDays(ts, 1));
  const to = _DATE_KEY_FMT.format(addDays(ts, 1));
  return `${base}&from=${from}&to=${to}`;
}

/** Render a call duration as "12 min, 8 sec" / "8 sec" (no leading
 *  zero minute). Sub-minute calls drop the minute slot entirely. */
function formatDuration(durationSec: number): string {
  const totalSec = Math.max(0, Math.floor(durationSec));
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  if (minutes === 0) return `${seconds} sec`;
  return `${minutes} min, ${seconds} sec`;
}

/** Relative time with an absolute-time tooltip on hover.
 *
 *  Dashed underline + ``cursor-help`` make the affordance obvious; the
 *  shadcn ``Tooltip`` defaults to ``delayDuration={0}`` so the rep
 *  doesn't have to wait for the hover threshold. Falls back to the
 *  bare relative label when ``iso`` is null (rare — backend couldn't
 *  compute a display_timestamp). */
function TimestampLabel({ iso, relative }: { iso: string | null | undefined; relative: string }) {
  if (!iso) return <span>{relative}</span>;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help border-b border-dashed border-current/40 align-baseline">
          {relative}
        </span>
      </TooltipTrigger>
      <TooltipContent side="left">{formatAbsolute(iso)}</TooltipContent>
    </Tooltip>
  );
}

interface DealHistoryPanelProps {
  /** Active TODO key. Drives the fetch query-key. */
  todoId: string;
  /** Toggled from the drawer header; the panel is rendered (and the
   *  query enabled) only when true. Toggling off unmounts the panel
   *  and abandons any in-flight request via React Query's enabled
   *  flag, which is the right blast radius for a noisy timeline
   *  query. */
  enabled: boolean;
}

export function DealHistoryPanel({ todoId, enabled }: DealHistoryPanelProps) {
  const { data, isLoading, isError } = useDealHistory(todoId, enabled);

  if (!enabled) return null;

  return (
    // Inverted-contrast container — dark in light mode, light in dark
    // mode — so the History block visibly stands apart from the rest
    // of the drawer chrome. Inner rows (activity / note / email) keep
    // their own backgrounds (``bg-background``, ``bg-yellow-50``, etc.)
    // so the content surface stays readable; only the outer chrome
    // inverts.
    <div className="rounded-md border bg-foreground text-background px-3 py-2.5 space-y-2">
      <div className="text-[11px] font-medium uppercase tracking-wider text-background/80 flex items-center gap-1.5">
        <HistoryIcon className="h-3 w-3" aria-hidden />
        Deal history
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-xs text-background/80">
          <Loader2Icon className="h-3.5 w-3.5 animate-spin" />
          Loading activities…
        </div>
      ) : isError ? (
        <div className="text-xs text-destructive">Couldn't load deal history.</div>
      ) : !data || data.items.length === 0 ? (
        <div className="text-xs text-background/80">Nothing logged on this deal yet.</div>
      ) : (
        <div className="space-y-2">
          {data.items.map((item, idx) => (
            <DealHistoryRow key={historyItemKey(item, idx)} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Stable per-row React key composed from the item's own identity
 *  fields — ``kind`` + ``source_system`` + ``timestamp``. The backend
 *  returns one row per contact/activity/note within a deal's history,
 *  and that tuple is unique within a deal's lifetime (a hickory_contact
 *  and a PD activity sharing a timestamp diverge on either ``kind`` or
 *  ``source_system``). Falls back to ``idx`` only when the row is
 *  malformed (no timestamp at all) — the previous index-only key
 *  broke React's reconciliation if items ever reorder. */
function historyItemKey(item: DealHistoryItem, idx: number): string {
  const ts = item.timestamp ?? item.display_timestamp;
  if (ts) {
    return `${item.kind}:${item.source_system ?? "pd"}:${ts}`;
  }
  return `idx:${idx}`;
}

function DealHistoryRow({ item }: { item: DealHistoryItem }) {
  // ``display_timestamp`` is the field the rep actually wants to read:
  // completion time for done activities, planned due_date for un-done
  // activities, event time for notes/emails. Falls back to raw timestamp
  // when the backend couldn't compute one.
  const tsSource = item.display_timestamp ?? item.timestamp ?? null;
  const tsLabel = tsSource
    ? formatDistanceToNowStrict(parseISO(tsSource), { addSuffix: true })
    : "no date";

  if (item.kind === "note") {
    return <NoteRow item={item} tsLabel={tsLabel} tsSource={tsSource} />;
  }
  if (item.kind === "email") {
    return <EmailRow item={item} tsLabel={tsLabel} tsSource={tsSource} />;
  }
  return <ActivityRow item={item} tsLabel={tsLabel} tsSource={tsSource} />;
}

function ActivityRow({
  item,
  tsLabel,
  tsSource,
}: {
  item: DealHistoryItem;
  tsLabel: string;
  tsSource: string | null;
}) {
  const typeLabel = item.activity_type
    ? humanizeActivityType(item.activity_type)
    : "Activity";

  const containerClass = item.overdue
    ? "rounded-md border-2 border-red-300 bg-red-50/40 px-2.5 py-2 space-y-1"
    : "rounded-md border bg-background px-2.5 py-2 space-y-1";

  // Attached note: PD-style yellow block nested inside the activity
  // row when a note's body explicitly links back to this activity
  // (see ``selected=<activity_id>`` URL match in the backend).
  // Sanitized with DOMPurify — note bodies are HTML authored by
  // any PD user with deal access.
  const sanitizedAttachedNote = item.attached_note_html
    ? DOMPurify.sanitize(item.attached_note_html)
    : null;

  // CALL chip enrichments. Only RC-sourced calls carry a session id —
  // PD-only activity rows don't get a deep-link. Duration + summary
  // come from ``rc_call_transcripts`` via the backend join; both are
  // ``null`` for PD-only rows and for RC rows whose transcript is
  // still pending.
  const isCall = (item.activity_type ?? "").toLowerCase() === "call";
  const callHref =
    isCall && item.call_id ? callsConversationUrl(item.call_id, tsSource) : null;
  const durationLabel =
    isCall && item.duration_sec != null && item.duration_sec > 0
      ? formatDuration(item.duration_sec)
      : null;
  const callSummary = isCall ? item.call_summary : null;

  // Render the type-label either as a plain span or a link to
  // ``/conversations``. Same typography either way — the underline +
  // colour shift make the affordance visible without breaking the
  // existing chip layout.
  const typeLabelNode = callHref ? (
    <a
      href={callHref}
      target="_blank"
      rel="noopener noreferrer"
      className="font-medium uppercase tracking-wider underline decoration-dotted underline-offset-2 hover:text-blue-600"
    >
      {typeLabel}
    </a>
  ) : (
    <span className="font-medium uppercase tracking-wider">{typeLabel}</span>
  );

  return (
    <div className={containerClass}>
      <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1">
          {item.done ? (
            <CheckCircle2Icon className="h-3 w-3 text-green-600" aria-hidden />
          ) : (
            <ClockIcon className="h-3 w-3" aria-hidden />
          )}
          {typeLabelNode}
          {durationLabel && (
            <span className="text-foreground/60">({durationLabel})</span>
          )}
          {isCommsActivity(item) && <DirectionBadge direction={item.direction} />}
          {item.overdue && (
            <span className="ml-1 inline-flex items-center gap-0.5 rounded-sm bg-red-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-red-700">
              <AlertTriangleIcon className="h-2.5 w-2.5" aria-hidden />
              Overdue
            </span>
          )}
        </span>
        <TimestampLabel iso={tsSource} relative={tsLabel} />
      </div>
      {item.subject && (
        <div className="text-xs font-medium text-foreground/90 break-words">
          {item.subject}
        </div>
      )}
      {item.description && (
        <div className="text-xs text-foreground/70 whitespace-pre-wrap break-words leading-snug">
          {item.description}
        </div>
      )}
      {callSummary && (
        <div className="mt-1.5 rounded-sm border border-muted bg-muted/30 px-2 py-1.5 text-xs text-foreground/80 whitespace-pre-wrap break-words leading-snug">
          <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground mb-0.5">
            Summary
          </div>
          {callSummary}
        </div>
      )}
      {sanitizedAttachedNote && (
        <div className="mt-1.5 rounded-sm border border-yellow-200 bg-yellow-50 px-2 py-1.5 text-xs text-foreground/85 [&_a]:text-blue-600 [&_a]:underline whitespace-normal break-words leading-snug">
          <div
            dangerouslySetInnerHTML={{ __html: sanitizedAttachedNote }}
          />
        </div>
      )}
    </div>
  );
}

function NoteRow({
  item,
  tsLabel,
  tsSource,
}: {
  item: DealHistoryItem;
  tsLabel: string;
  tsSource: string | null;
}) {
  // PD note bodies are HTML (PD's web UI is a rich-text editor). Any
  // Pipedrive user with deal access can author arbitrary markup
  // through that editor — including script tags and event handlers —
  // and the payload arrives via webhook into ``landing.pipedrive_raw``
  // unchanged. Render through DOMPurify so a malicious or compromised
  // PD account can't drop XSS payloads that fire in every rep's
  // session that opens this drawer.
  const sanitized = item.note_html ? DOMPurify.sanitize(item.note_html) : null;
  return (
    <div className="rounded-md border border-yellow-200 bg-yellow-50 px-2.5 py-2 space-y-1">
      <div className="flex items-center justify-between gap-2 text-[10px] text-yellow-900/70">
        <span className="flex items-center gap-1">
          <StickyNoteIcon className="h-3 w-3" aria-hidden />
          <span className="font-medium uppercase tracking-wider">Note</span>
        </span>
        <TimestampLabel iso={tsSource} relative={tsLabel} />
      </div>
      {sanitized && (
        <div
          className="text-xs text-foreground/85 whitespace-normal break-words leading-snug [&_a]:text-blue-600 [&_a]:underline"
          dangerouslySetInnerHTML={{ __html: sanitized }}
        />
      )}
    </div>
  );
}

function EmailRow({
  item,
  tsLabel,
  tsSource,
}: {
  item: DealHistoryItem;
  tsLabel: string;
  tsSource: string | null;
}) {
  return (
    <div className="rounded-md border bg-background px-2.5 py-2 space-y-1">
      <div className="flex items-center justify-between gap-2 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <MailIcon className="h-3 w-3" aria-hidden />
          <span className="font-medium uppercase tracking-wider">Email</span>
          <DirectionBadge direction={item.direction} />
        </span>
        <TimestampLabel iso={tsSource} relative={tsLabel} />
      </div>
      {item.email_from && (
        <div className="text-[10px] text-foreground/60">
          From: <span className="text-foreground/80">{item.email_from}</span>
          {item.email_to && (
            <>
              {" "}
              · To: <span className="text-foreground/80">{item.email_to}</span>
            </>
          )}
        </div>
      )}
      {item.subject && (
        <div className="text-xs font-medium text-foreground/90 break-words">
          {item.subject}
        </div>
      )}
      {item.description && (
        <div className="text-xs text-foreground/70 whitespace-pre-wrap break-words leading-snug">
          {item.description}
        </div>
      )}
    </div>
  );
}

/** Pretty-print PD's activity-type slug ("call" → "Call"). PD ships
 *  a handful of built-in types plus tenant-configured custom types;
 *  the slug is already lowercased, so a Title-case is enough. */
function humanizeActivityType(slug: string): string {
  return slug
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Render an inbound / outbound chip on email / sms / call rows. The
 *  rep needs to identify direction at a glance — colored arrow + word.
 *  When ``direction`` is null (PD-activity-only rows that bypass
 *  hickory_contacts; some PD payloads have no direction signal), render
 *  a neutral chip so the row still gets a "kind of comms" indicator —
 *  once hickory_contacts becomes the single source of truth every
 *  row will carry a real direction. */
function DirectionBadge({ direction }: { direction: "inbound" | "outbound" | null }) {
  if (direction === "inbound") {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-sm bg-green-100 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-green-800">
        <ArrowDownLeftIcon className="h-2.5 w-2.5" aria-hidden />
        In
      </span>
    );
  }
  if (direction === "outbound") {
    return (
      <span className="inline-flex items-center gap-0.5 rounded-sm bg-blue-100 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-blue-800">
        <ArrowUpRightIcon className="h-2.5 w-2.5" aria-hidden />
        Out
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-0.5 rounded-sm bg-muted px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground"
      title="Direction not recorded"
    >
      —
    </span>
  );
}

/** Channels that should always carry a direction badge. Notes / tasks /
 *  meetings / deadlines / changelog rows are not comms — they don't
 *  get a badge. */
const _COMMS_CHANNELS = new Set(["call", "email", "sms"]);

function isCommsActivity(item: DealHistoryItem): boolean {
  const t = (item.activity_type ?? "").toLowerCase();
  return _COMMS_CHANNELS.has(t);
}

/** Count calls / emails / SMSs in a history list. Both ``email`` items
 *  (canonical Gmail) and PD-activity rows with ``activity_type === "email"``
 *  contribute to the email tally — the rep doesn't care which path a
 *  message took to land in PD. Notes and tasks don't count. */
function commsCounts(items: DealHistoryItem[]): {
  calls: number;
  emails: number;
  smses: number;
} {
  let calls = 0;
  let emails = 0;
  let smses = 0;
  for (const it of items) {
    if (it.kind === "activity" && !it.done) continue;
    const t = (it.activity_type ?? "").toLowerCase();
    if (it.kind === "email" || t === "email") emails += 1;
    else if (t === "call") calls += 1;
    else if (t === "sms") smses += 1;
  }
  return { calls, emails, smses };
}

function pluralize(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/** Build the comms-count summary that's appended to the History card
 *  title — e.g. "1 call" / "2 calls, 1 email, 1 SMS". Returns null when
 *  nothing counted so the title falls back to bare "History". */
function summarizeComms(items: DealHistoryItem[]): string | null {
  const { calls, emails, smses } = commsCounts(items);
  const parts: string[] = [];
  if (calls > 0) parts.push(pluralize(calls, "call", "calls"));
  if (emails > 0) parts.push(pluralize(emails, "email", "emails"));
  if (smses > 0) parts.push(pluralize(smses, "SMS", "SMSs"));
  if (parts.length === 0) return null;
  return parts.join(", ");
}

/** Compact "History" card surfaced in the drawer in place of the old
 *  Show/Hide button. Always-mounted (no click-to-fetch) so the rep can
 *  see the most-recent activity at a glance. "Show All" expands the
 *  card to the full timeline; "Collapse" returns to the one-row view.
 *
 *  The most-recent entry is the LAST item in ``data.items`` — the
 *  backend orders oldest → newest so the list reads like an email
 *  thread. */
export function HistoryCard({ todoId, contextLabel }: { todoId: string; contextLabel: "deal" | "lead" }) {
  const [expanded, setExpanded] = useState(false);
  const { data, isLoading, isError } = useDealHistory(todoId, true);

  const items = data?.items ?? [];
  const mostRecent = items.length > 0 ? items[items.length - 1] : null;
  const emptyLabel = `Nothing logged on this ${contextLabel} yet.`;
  // Comms summary replaces the old prior-contact banner: surface the
  // call/email/SMS count directly in the History title so the rep can
  // see "have I touched this person before?" without an extra block.
  const commsSummary = summarizeComms(items);

  return (
    // Inverted-contrast container — same chrome as ``DealHistoryPanel``
    // above. See that component's note for the rationale + the inner-
    // row contract (inner rows keep their own backgrounds).
    <div className="rounded-md border bg-foreground text-background px-3 py-2.5 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] font-medium uppercase tracking-wider text-background/80 flex items-center gap-1.5">
          <HistoryIcon className="h-3 w-3" aria-hidden />
          <span>History</span>
          {commsSummary && (
            <span className="normal-case tracking-normal text-background/70">
              ({commsSummary})
            </span>
          )}
        </div>
        {items.length > 1 && (
          <Button
            size="sm"
            variant="outline"
            className="h-6 px-2 text-[10px] bg-background text-foreground hover:bg-background/90"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? "Collapse" : `Show all (${items.length})`}
          </Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center gap-2 text-xs text-background/80">
          <Loader2Icon className="h-3.5 w-3.5 animate-spin" />
          Loading…
        </div>
      ) : isError ? (
        <div className="text-xs text-destructive">Couldn't load history.</div>
      ) : items.length === 0 ? (
        <div className="text-xs text-background/80">{emptyLabel}</div>
      ) : expanded ? (
        <div className="space-y-2">
          {items.map((item, idx) => (
            <DealHistoryRow key={historyItemKey(item, idx)} item={item} />
          ))}
        </div>
      ) : (
        mostRecent && <DealHistoryRow item={mostRecent} />
      )}
    </div>
  );
}
